/**
 * 智能文档工作台 API 门面。
 * 数字 projectId 走真实后端；非数字或 VITE_SMART_DOC_USE_MOCK=true 时回退 mock（Figma demo）。
 */
import http, { getBrowserStoredAccessToken, request } from './http';
import type {
  ApplySkillExecutionRequest,
  ApplySkillExecutionResult,
  ResolveSkillApplyTargetRequest,
  ResolveSkillApplyTargetResult,
  SmartDocOutputIntentRequest,
  SmartDocOutputIntentResult,
  CreateDocumentResult,
  CreateDocumentSkillRequest,
  DocumentSkillDetail,
  DocumentSkillListItem,
  DocumentTemplateListRequest,
  DocumentTemplateListResponse,
  DocumentWorkbenchSnapshot,
  WorkbenchDocumentDetail,
  ImportDocumentSkillRequest,
  ImportDocumentSkillResult,
  ParseSkillFromDialogueRequest,
  ParseSkillFromDialogueResult,
  SaveAsTemplateBasicInfo,
  SaveAsTemplateExtractResult,
  SaveAsTemplateGenerateResult,
  SaveAsTemplateParseResult,
  SaveAsTemplatePreviewInfo,
  SaveAsTemplateRecognizeResult,
  SaveAsTemplateRequest,
  SaveAsTemplateResult,
  SaveAsTemplateReviewInfo,
  SaveAsTemplateStructureInfo,
  SkillDialogueRequest,
  SkillDialogueResult,
  SkillAcceptanceRunResult,
  SkillExecutionDetail,
  StartSkillExecutionRequest,
  StartTemplateGenerationRequest,
  TemplateGenerationMessageRequest,
  TemplateGenerationResult,
  TemplatePreviewResult,
  UpdateDocumentSkillContentRequest,
} from '@/types/documentWorkbench';
import * as mock from './documentWorkbenchMock';

interface Result<T> {
  success?: boolean;
  message?: string;
  data: T;
}

function useMock(projectId?: string | number, entityId?: string): boolean {
  const flag = String(import.meta.env.VITE_SMART_DOC_USE_MOCK || '').toLowerCase();
  if (flag === 'true' || flag === '1') return true;
  if (projectId != null && !/^\d+$/.test(String(projectId))) return true;
  if (entityId != null && !/^\d+$/.test(String(entityId))) return true;
  return false;
}

async function unwrap<T>(promise: Promise<Result<T>>): Promise<T> {
  const res = await promise;
  return res.data;
}

export async function getDocumentWorkbenchSnapshot(
  projectId: string,
  documentId?: string,
): Promise<DocumentWorkbenchSnapshot> {
  if (useMock(projectId)) return mock.getDocumentWorkbenchSnapshot(projectId);
  return unwrap(
    request<Result<DocumentWorkbenchSnapshot>>(
      http.get(`/v1/projects/${projectId}/document-workbench/snapshot`, {
        params: documentId ? { documentId } : undefined,
      }),
    ),
  );
}

export async function getWorkbenchDocumentDetail(
  documentId: string,
): Promise<WorkbenchDocumentDetail> {
  if (useMock(undefined, documentId)) {
    return {
      id: documentId,
      title: 'Mock 文档',
      statusLabel: '文件状态：可更新',
      statusDescription: 'Markdown 在线预览',
      contentHtml: '<h1>Mock</h1><p>示例内容</p>',
      contentMarkdown: '# Mock\n\n示例内容',
    };
  }
  return unwrap(
    request<Result<WorkbenchDocumentDetail>>(http.get(`/v1/documents/${documentId}`)),
  );
}

export async function updateWorkbenchDocumentContent(
  documentId: string,
  contentHtml: string,
): Promise<WorkbenchDocumentDetail> {
  if (useMock(undefined, documentId)) {
    return {
      id: documentId,
      title: 'Mock 文档',
      statusLabel: '文件状态：可更新',
      statusDescription: 'Markdown 在线编辑',
      contentHtml,
      contentMarkdown: contentHtml.replace(/<[^>]+>/g, ''),
    };
  }
  return unwrap(
    request<Result<WorkbenchDocumentDetail>>(
      http.put(`/v1/documents/${documentId}/content`, { contentHtml }),
    ),
  );
}

export async function getDocumentTemplates(
  req: DocumentTemplateListRequest,
): Promise<DocumentTemplateListResponse> {
  if (useMock(req.projectId)) return mock.getDocumentTemplates(req);
  return unwrap(
    request<Result<DocumentTemplateListResponse>>(
      http.get(`/v1/projects/${req.projectId}/document-templates`, {
        params: {
          scope: req.scope,
          keyword: req.keyword,
          status: req.status,
          documentType: req.documentType,
          filterProjectId: req.filterProjectId,
          sort: req.sort,
          creatorUserId: req.creatorUserId,
        },
      }),
    ),
  );
}

export async function createDocumentTemplate(
  projectId: string,
  body: import('@/types/documentWorkbench').CreateDocumentTemplateRequest,
): Promise<import('@/types/documentWorkbench').DocumentTemplateDetail> {
  if (useMock(projectId)) {
    return {
      id: `mock-tpl-${Date.now()}`,
      title: body.title,
      documentType: body.documentType,
      description: body.description,
      version: '1.0.0',
      status: 'published',
      saveScope: body.saveScope,
      skillCount: body.selectedSkillIds?.length || 0,
      canEdit: true,
      canPublish: body.saveScope !== 'mine',
      canDelete: true,
    };
  }
  return unwrap(
    request<Result<import('@/types/documentWorkbench').DocumentTemplateDetail>>(
      http.post(`/v1/projects/${projectId}/document-templates`, body),
    ),
  );
}

export async function rebuildDocumentTemplateGenerationProfile(
  templateId: string,
): Promise<import('@/types/documentWorkbench').DocumentTemplateDetail> {
  return unwrap(
    request<Result<import('@/types/documentWorkbench').DocumentTemplateDetail>>(
      http.post(`/v1/document-templates/${templateId}/rebuild-generation-profile`),
    ),
  );
}

export async function getDocumentTemplateDetail(
  templateId: string,
): Promise<import('@/types/documentWorkbench').DocumentTemplateDetail> {
  if (useMock(undefined, templateId)) {
    return {
      id: templateId,
      title: 'Mock 模板',
      version: '1.0.0',
      status: 'published',
      saveScope: 'mine',
      skillCount: 0,
      canEdit: true,
      canPublish: false,
    };
  }
  return unwrap(
    request<Result<import('@/types/documentWorkbench').DocumentTemplateDetail>>(
      http.get(`/v1/document-templates/${templateId}`),
    ),
  );
}

export async function renameDocumentTemplate(
  templateId: string,
  title: string,
): Promise<import('@/types/documentWorkbench').DocumentTemplateDetail> {
  if (useMock(undefined, templateId)) {
    return {
      id: templateId,
      title,
      version: '1.0.0',
      status: 'published',
      saveScope: 'mine',
      skillCount: 0,
      canEdit: true,
      canDelete: true,
    };
  }
  return unwrap(
    request<Result<import('@/types/documentWorkbench').DocumentTemplateDetail>>(
      http.post(`/v1/document-templates/${templateId}/rename`, { title }),
    ),
  );
}

export async function publishDocumentTemplate(
  templateId: string,
  body?: import('@/types/documentWorkbench').PublishDocumentTemplateRequest,
): Promise<import('@/types/documentWorkbench').DocumentTemplateDetail> {
  if (useMock(undefined, templateId)) {
    return {
      id: templateId,
      title: 'Mock 模板',
      version: '1.0.1',
      status: 'published',
      saveScope: body?.targetScope || 'mine',
      skillCount: 0,
    };
  }
  return unwrap(
    request<Result<import('@/types/documentWorkbench').DocumentTemplateDetail>>(
      http.post(`/v1/document-templates/${templateId}/publish`, body || {}),
    ),
  );
}

export async function disableDocumentTemplate(
  templateId: string,
): Promise<import('@/types/documentWorkbench').DocumentTemplateDetail> {
  if (useMock(undefined, templateId)) {
    return {
      id: templateId,
      title: 'Mock 模板',
      version: '1.0.0',
      status: 'disabled',
      saveScope: 'mine',
      skillCount: 0,
    };
  }
  return unwrap(
    request<Result<import('@/types/documentWorkbench').DocumentTemplateDetail>>(
      http.post(`/v1/document-templates/${templateId}/disable`),
    ),
  );
}

export async function archiveDocumentTemplate(
  templateId: string,
): Promise<import('@/types/documentWorkbench').DocumentTemplateDetail> {
  if (useMock(undefined, templateId)) {
    return {
      id: templateId,
      title: 'Mock 模板',
      version: '1.0.0',
      status: 'archived',
      saveScope: 'mine',
      skillCount: 0,
    };
  }
  return unwrap(
    request<Result<import('@/types/documentWorkbench').DocumentTemplateDetail>>(
      http.post(`/v1/document-templates/${templateId}/archive`),
    ),
  );
}

export async function deleteDocumentTemplate(templateId: string): Promise<void> {
  if (useMock(undefined, templateId)) return;
  await unwrap(request<Result<null>>(http.delete(`/v1/document-templates/${templateId}`)));
}

export async function applyTemplateStructure(
  documentId: string,
  templateId: string,
  overwriteExisting = false,
): Promise<import('@/types/documentWorkbench').ApplyTemplateStructureResult> {
  if (useMock(undefined, documentId)) {
    return {
      documentId,
      templateId,
      insertedSectionCount: 1,
      contentHtml: '<h2>应用章节</h2><p>{{ 应用章节 }}</p>',
    };
  }
  return unwrap(
    request<Result<import('@/types/documentWorkbench').ApplyTemplateStructureResult>>(
      http.post(`/v1/documents/${documentId}/apply-template/${templateId}`, {
        overwriteExisting,
      }),
    ),
  );
}

export async function templateDialogue(
  projectId: string,
  body: import('@/types/documentWorkbench').TemplateDialogueRequest,
): Promise<import('@/types/documentWorkbench').TemplateDialogueResult> {
  if (useMock(projectId)) {
    const msg = body.userMessage || '';
    const enough = msg.length >= 24 || (body.history?.length || 0) >= 2;
    const conversationId = body.conversationId || `mock-tpl-${Date.now()}`;
    if (body.confirmSave && body.draft) {
      return {
        conversationId,
        phase: 'saved',
        assistantReply: '已保存为我的模版 Draft',
        draft: body.draft,
        template: {
          id: `tpl-${Date.now()}`,
          title: body.draft.title || '对话模版',
          version: '0.1.0',
          status: 'draft',
          saveScope: 'mine',
          skillCount: 0,
        },
      };
    }
    if (!enough) {
      return {
        conversationId,
        phase: 'discuss',
        assistantReply: '请补充模版名称、文档类型与章节大纲。',
        warnings: ['mock：引导补全'],
      };
    }
    return {
      conversationId,
      phase: 'draft_ready',
      assistantReply: '已整理模版草稿，确认后保存到「我的模板」。',
      draft: {
        title: msg.slice(0, 24) || '新建模版',
        documentType: 'requirement',
        outlineChapters: ['背景与目标', '范围说明', '关键内容', '验收标准'],
        contentHtml:
          '<h2>背景与目标</h2><p>{{ 背景与目标 }}</p><h2>范围说明</h2><p>{{ 范围说明 }}</p>',
      },
    };
  }
  return unwrap(
    request<Result<import('@/types/documentWorkbench').TemplateDialogueResult>>(
      http.post(`/v1/projects/${projectId}/document-templates/dialogue`, body, {
        timeout: 120000,
      }),
    ),
  );
}

export async function importDocumentTemplate(
  projectId: string,
  body: import('@/types/documentWorkbench').ImportDocumentTemplateRequest,
): Promise<import('@/types/documentWorkbench').ImportDocumentTemplateResult> {
  if (useMock(projectId)) {
    if (!body.packageContent) {
      return { success: false, fixableErrors: ['packageContent 不能为空'] };
    }
    return {
      success: true,
      template: {
        id: `tpl-import-${Date.now()}`,
        title: body.title || '导入模版',
        version: '0.1.0',
        status: 'draft',
        saveScope: 'mine',
        skillCount: 0,
      },
      warnings: ['mock：已导入 Draft'],
    };
  }
  return unwrap(
    request<Result<import('@/types/documentWorkbench').ImportDocumentTemplateResult>>(
      http.post(`/v1/projects/${projectId}/document-templates/import`, body),
    ),
  );
}

export async function createBlankDocument(
  projectId: string,
  title?: string,
): Promise<CreateDocumentResult> {
  if (useMock(projectId)) return mock.createBlankDocument(projectId);
  return unwrap(
    request<Result<CreateDocumentResult>>(
      http.post(`/v1/projects/${projectId}/documents/blank`, null, {
        params: title ? { title } : undefined,
      }),
    ),
  );
}

export async function createDocumentFromUpload(
  projectId: string,
  file: File,
  options?: { templateBackup?: boolean },
): Promise<CreateDocumentResult> {
  if (useMock(projectId)) {
    return {
      documentId: `upload-${Date.now()}`,
      title: file.name.replace(/\.[^.]+$/, '') || '导入文档',
    };
  }
  const form = new FormData();
  form.append('file', file);
  const params = options?.templateBackup ? { templateBackup: true } : undefined;
  return unwrap(
    request<Result<CreateDocumentResult>>(
      http.post(`/v1/projects/${projectId}/documents/from-upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
        params,
      }),
    ),
  );
}

export async function getTemplatePreview(templateId: string): Promise<TemplatePreviewResult> {
  if (useMock(undefined, templateId)) return mock.getTemplatePreview(templateId);
  return unwrap(
    request<Result<TemplatePreviewResult>>(
      http.get(`/v1/document-templates/${templateId}/preview`),
    ),
  );
}

export async function createDocumentFromTemplate(
  projectId: string,
  templateId: string,
): Promise<CreateDocumentResult> {
  if (useMock(projectId)) return mock.createDocumentFromTemplate(projectId, templateId);
  return unwrap(
    request<Result<CreateDocumentResult>>(
      http.post(`/v1/projects/${projectId}/documents/from-template/${templateId}`),
    ),
  );
}

export async function downloadWorkbenchDocument(
  documentId: string,
): Promise<{ downloadUrl: string }> {
  if (useMock(undefined, documentId)) return mock.downloadWorkbenchDocument(documentId);
  return unwrap(
    request<Result<{ downloadUrl: string }>>(
      http.get(`/v1/documents/${documentId}/download`),
    ),
  );
}

/** 带鉴权下载工作台文档（优先 Office 原格式，否则 HTML） */
export async function downloadWorkbenchDocumentFile(
  documentId: string,
  fileNameHint?: string,
): Promise<void> {
  if (useMock(undefined, documentId)) {
    const mockResult = await mock.downloadWorkbenchDocument(documentId);
    window.open(mockResult.downloadUrl, '_blank');
    return;
  }
  let exportPath = `/v1/documents/${documentId}/export.html`;
  let preferSource = false;
  let format = 'html';
  try {
    const meta = await unwrap(
      request<
        Result<{
          downloadUrl?: string;
          format?: string;
          hasSourceFile?: boolean;
          fileName?: string;
        }>
      >(http.get(`/v1/documents/${documentId}/download`)),
    );
    if (meta?.hasSourceFile || (meta?.format && meta.format !== 'html')) {
      exportPath = `/v1/documents/${documentId}/export.source`;
      preferSource = true;
      format = meta.format || 'bin';
    }
    if (meta?.fileName) fileNameHint = meta.fileName;
  } catch {
    /* 元数据失败时回退 HTML */
  }
  const res = await http.get(exportPath, {
    responseType: 'blob',
  });
  const defaultType = preferSource
    ? 'application/octet-stream'
    : 'text/html';
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: defaultType });
  if (blob.type && blob.type.includes('application/json')) {
    const text = await blob.text();
    throw new Error(text || '下载失败');
  }
  let fileName =
    (fileNameHint || `document-${documentId}`).trim() || `document-${documentId}`;
  if (preferSource) {
    const ext = format && format !== 'bin' ? `.${format}` : '';
    if (ext && !fileName.toLowerCase().endsWith(ext)) {
      if (!/\.(docx?|pptx?|xlsx?|bin)$/i.test(fileName)) {
        fileName = `${fileName}${ext || '.bin'}`;
      }
    }
  } else if (!/\.html?$/i.test(fileName)) {
    fileName = `${fileName}.html`;
  }
  const disposition = String(res.headers?.['content-disposition'] || '');
  const m = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  if (m) {
    try {
      fileName = decodeURIComponent((m[1] || m[2] || fileName).trim());
    } catch {
      /* keep hint */
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** 带鉴权导出模版（优先 Office 原格式，否则 HTML） */
export async function downloadDocumentTemplateFile(
  templateId: string,
  fileNameHint?: string,
): Promise<void> {
  if (useMock(undefined, templateId)) {
    return;
  }
  let exportPath = `/v1/document-templates/${templateId}/export.html`;
  let preferSource = false;
  let format = 'html';
  try {
    const meta = await unwrap(
      request<
        Result<{
          downloadUrl?: string;
          format?: string;
          hasSourceFile?: boolean;
          fileName?: string;
        }>
      >(http.get(`/v1/document-templates/${templateId}/download`)),
    );
    if (meta?.hasSourceFile || (meta?.format && meta.format !== 'html')) {
      exportPath = `/v1/document-templates/${templateId}/export.source`;
      preferSource = true;
      format = meta.format || 'bin';
    }
    if (meta?.fileName) fileNameHint = meta.fileName;
  } catch {
    /* fallback html */
  }
  const res = await http.get(exportPath, { responseType: 'blob' });
  const blob =
    res.data instanceof Blob
      ? res.data
      : new Blob([res.data], { type: preferSource ? 'application/octet-stream' : 'text/html' });
  if (blob.type && blob.type.includes('application/json')) {
    const text = await blob.text();
    throw new Error(text || '导出失败');
  }
  let fileName = (fileNameHint || `template-${templateId}`).trim() || `template-${templateId}`;
  if (preferSource) {
    const ext = format && format !== 'bin' ? `.${format}` : '';
    if (ext && !fileName.toLowerCase().endsWith(ext) && !/\.(docx?|pptx?|xlsx?)$/i.test(fileName)) {
      fileName = `${fileName}${ext}`;
    }
  } else if (!/\.html?$/i.test(fileName)) {
    fileName = `${fileName}.html`;
  }
  const disposition = String(res.headers?.['content-disposition'] || '');
  const m = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  if (m) {
    try {
      fileName = decodeURIComponent((m[1] || m[2] || fileName).trim());
    } catch {
      /* keep */
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function getSaveAsTemplateBasicInfo(
  documentId: string,
): Promise<SaveAsTemplateBasicInfo> {
  if (useMock(undefined, documentId)) return mock.getSaveAsTemplateBasicInfo(documentId);
  return unwrap(
    request<Result<SaveAsTemplateBasicInfo>>(
      http.get(`/v1/documents/${documentId}/save-as-template/basic`),
    ),
  );
}

/** 模版提炼长任务（含 LLM），默认 axios 60s 会提前断开 */
const SAVE_AS_TEMPLATE_LLM_TIMEOUT_MS = 300_000;
const SAVE_AS_TEMPLATE_FAST_TIMEOUT_MS = 120_000;

/** 四阶段编排：parse → recognize → generate（兼容向导后台任务） */
export async function extractSaveAsTemplate(
  documentId: string,
): Promise<SaveAsTemplateExtractResult> {
  if (useMock(undefined, documentId)) return mock.extractSaveAsTemplate(documentId);
  return unwrap(
    request<Result<SaveAsTemplateExtractResult>>(
      http.post(`/v1/documents/${documentId}/save-as-template/extract`, null, {
        timeout: SAVE_AS_TEMPLATE_LLM_TIMEOUT_MS,
      }),
    ),
  );
}

export async function parseSaveAsTemplate(
  documentId: string,
): Promise<SaveAsTemplateParseResult> {
  return unwrap(
    request<Result<SaveAsTemplateParseResult>>(
      http.post(`/v1/documents/${documentId}/save-as-template/parse`, null, {
        timeout: SAVE_AS_TEMPLATE_FAST_TIMEOUT_MS,
      }),
    ),
  );
}

export async function recognizeSaveAsTemplateStructure(
  documentId: string,
): Promise<SaveAsTemplateRecognizeResult> {
  return unwrap(
    request<Result<SaveAsTemplateRecognizeResult>>(
      http.post(`/v1/documents/${documentId}/save-as-template/recognize-structure`, null, {
        timeout: SAVE_AS_TEMPLATE_FAST_TIMEOUT_MS,
      }),
    ),
  );
}

export async function generateSaveAsTemplate(
  documentId: string,
): Promise<SaveAsTemplateGenerateResult> {
  return unwrap(
    request<Result<SaveAsTemplateGenerateResult>>(
      http.post(`/v1/documents/${documentId}/save-as-template/generate`, null, {
        timeout: SAVE_AS_TEMPLATE_LLM_TIMEOUT_MS,
      }),
    ),
  );
}

export async function getSaveAsTemplateStructure(
  documentId: string,
): Promise<SaveAsTemplateStructureInfo> {
  if (useMock(undefined, documentId)) return mock.getSaveAsTemplateStructure(documentId);
  return unwrap(
    request<Result<SaveAsTemplateStructureInfo>>(
      http.get(`/v1/documents/${documentId}/save-as-template/structure`),
    ),
  );
}

export async function getSaveAsTemplateReview(
  documentId: string,
): Promise<SaveAsTemplateReviewInfo> {
  if (useMock(undefined, documentId)) return mock.getSaveAsTemplateReview(documentId);
  return unwrap(
    request<Result<SaveAsTemplateReviewInfo>>(
      http.get(`/v1/documents/${documentId}/save-as-template/review`),
    ),
  );
}

export async function getSaveAsTemplatePreview(
  documentId: string,
): Promise<SaveAsTemplatePreviewInfo> {
  if (useMock(undefined, documentId)) return mock.getSaveAsTemplatePreview(documentId);
  return unwrap(
    request<Result<SaveAsTemplatePreviewInfo>>(
      http.get(`/v1/documents/${documentId}/save-as-template/preview`),
    ),
  );
}

export async function saveWorkbenchDocumentAsTemplate(
  documentId: string,
  body: SaveAsTemplateRequest,
): Promise<SaveAsTemplateResult> {
  if (useMock(undefined, documentId)) return mock.saveWorkbenchDocumentAsTemplate(documentId, body);
  return unwrap(
    request<Result<SaveAsTemplateResult>>(
      http.post(`/v1/documents/${documentId}/save-as-template`, body),
    ),
  );
}

export async function deleteWorkbenchDocument(
  documentId: string,
): Promise<{ success: boolean }> {
  if (useMock(undefined, documentId)) return mock.deleteWorkbenchDocument(documentId);
  return unwrap(
    request<Result<{ success: boolean }>>(
      http.delete(`/v1/documents/${documentId}`),
    ),
  );
}

/** 将工作台文档移入指定项目工作台 */
export async function moveWorkbenchDocumentToProject(
  documentId: string,
  projectId: string,
): Promise<CreateDocumentResult> {
  if (useMock(projectId, documentId)) {
    return {
      documentId,
      title: '已移入项目',
      routeUrl: `/project/${projectId}/smart-document?documentId=${documentId}`,
    };
  }
  return unwrap(
    request<Result<CreateDocumentResult>>(
      http.post(`/v1/documents/${documentId}/move-to-project`, { projectId: Number(projectId) }),
    ),
  );
}

/** 智能文档页：判定本轮输出意图 */
export async function resolveSmartDocOutputIntent(
  projectId: string,
  body?: SmartDocOutputIntentRequest,
): Promise<SmartDocOutputIntentResult> {
  const ws = projectId && /^\d+$/.test(projectId) ? projectId : '0';
  if (useMock(ws)) {
    const t = String(body?.userMessage || '');
    if (/(?:周报|日报|纪要|总结|方案|报告)/.test(t) && /(?:生成|写|起草|输出|帮我)/.test(t)) {
      return { decision: 'create_document', confidence: 'high', suggestedTitle: '项目文档' };
    }
    if (/(?:合入|追加|写入当前|加到当前)/.test(t) && body?.hasCurrentDocument) {
      return { decision: 'merge_current', confidence: 'high' };
    }
    if (/(?:修改|修订|更正|润色|修正)/.test(t) && body?.hasCurrentDocument) {
      return { decision: 'revise_current', confidence: 'high' };
    }
    return {
      decision: 'clarify',
      confidence: 'low',
      clarifyQuestion: '请问是新建工作台文档、合入/修正当前文档，还是仅在对话回答？',
    };
  }
  return unwrap(
    request<Result<SmartDocOutputIntentResult>>(
      http.post(`/v1/projects/${ws}/smart-doc/output-intent`, body || {}),
    ),
  );
}

/** —— Skill —— */

export async function listDocumentSkills(
  projectId: string,
): Promise<DocumentSkillListItem[]> {
  if (useMock(projectId)) {
    return [
      {
        id: 'skill-sor-collab-2-1',
        code: 'sor_collab',
        name: 'SOR 协作生成',
        category: 'document_generation',
        description: '基于项目资料生成 SOR 章节草稿',
        version: '2.1',
        visibility: 'project',
        status: 'published',
      },
      {
        id: 'skill-sor-completeness-1-6',
        code: 'sor_completeness',
        name: 'SOR 完整性检查',
        category: 'quality_check',
        description: '检查必要章节与参数是否齐全',
        version: '1.6',
        visibility: 'project',
        status: 'published',
      },
    ];
  }
  return unwrap(
    request<Result<DocumentSkillListItem[]>>(
      http.get(`/v1/projects/${projectId}/document-skills`),
    ),
  );
}

export async function getDocumentSkill(skillId: string): Promise<DocumentSkillDetail> {
  if (useMock(undefined, skillId)) {
    return {
      id: skillId,
      code: skillId,
      name: skillId,
      category: 'document_generation',
      description: '',
      visibility: 'private',
      status: 'draft',
      currentVersion: '0.1.0',
    };
  }
  return unwrap(
    request<Result<DocumentSkillDetail>>(http.get(`/v1/document-skills/${skillId}`)),
  );
}

export async function createDocumentSkill(
  projectId: string,
  body: CreateDocumentSkillRequest,
): Promise<DocumentSkillDetail> {
  if (useMock(projectId)) {
    return {
      id: `skill-draft-${Date.now()}`,
      code: body.code,
      name: body.name,
      category: body.category,
      description: body.description || '',
      visibility: body.visibility || 'private',
      status: 'draft',
      currentVersion: '0.1.0',
      promptText: body.promptText,
      sopText: body.sopText,
      inputSchemaJson: body.inputSchemaJson,
      toolPermissionsJson: body.toolPermissionsJson,
      outputContractJson: body.outputContractJson,
    };
  }
  return unwrap(
    request<Result<DocumentSkillDetail>>(
      http.post(`/v1/projects/${projectId}/document-skills`, body),
    ),
  );
}

export async function parseSkillFromDialogue(
  projectId: string,
  body: ParseSkillFromDialogueRequest,
): Promise<ParseSkillFromDialogueResult> {
  if (useMock(projectId)) {
    return {
      code: 'mock_skill',
      name: body.dialogueText.slice(0, 20) || 'Mock Skill',
      category: body.category || 'document_generation',
      description: 'mock 解析',
      visibility: 'private',
      promptText: `根据需求生成文档：${body.dialogueText}`,
      sopText: '1. 理解需求\n2. 检索资料\n3. 生成草稿',
      parseSource: 'heuristic',
      warnings: ['mock 环境：规则解析'],
    };
  }
  return unwrap(
    request<Result<ParseSkillFromDialogueResult>>(
      http.post(`/v1/projects/${projectId}/document-skills/parse-from-dialogue`, body),
    ),
  );
}

export async function skillDialogue(
  projectId: string,
  body: SkillDialogueRequest,
  opts?: { signal?: AbortSignal },
): Promise<SkillDialogueResult> {
  if (useMock(projectId)) {
    const msg = body.userMessage || '';
    const enough = msg.length >= 40 || /步骤|SOP|输出|生成|检查/.test(msg) || (body.history?.length || 0) >= 2;
    const conversationId = body.conversationId || `mock-dlg-${Date.now()}`;
    if (!enough) {
      return {
        conversationId,
        phase: 'discuss',
        assistantReply:
          '请补充技能名称、适用场景、输入内容、处理步骤（SOP）以及期望输出格式。例如可参考「SOR 协作生成」或「完整性检查」这类目标来描述你的场景。',
        warnings: ['mock：引导补全'],
      };
    }
    return {
      conversationId,
      phase: 'draft_ready',
      assistantReply: '已根据对话整理出 Skill 草稿预览，请确认后保存到我的技能。',
      draft: {
        code: body.mode === 'optimize' ? 'optimized_skill' : 'mock_skill',
        name: msg.slice(0, 24) || (body.mode === 'optimize' ? '优化后的技能' : '新建技能'),
        category: body.category || 'document_generation',
        description: '由多轮对话生成的草稿',
        visibility: 'private',
        promptText: `请按以下需求执行：\n${msg}`,
        sopText: '1. 确认目标\n2. 检索资料\n3. 生成输出\n4. 等待确认写回',
        parseSource: 'heuristic',
      },
    };
  }
  return unwrap(
    request<Result<SkillDialogueResult>>(
      http.post(`/v1/projects/${projectId}/document-skills/dialogue`, body, {
        signal: opts?.signal,
        timeout: 120000,
      }),
    ),
  );
}

export async function updateDocumentSkillContent(
  skillId: string,
  body: UpdateDocumentSkillContentRequest,
): Promise<DocumentSkillDetail> {
  if (useMock(undefined, skillId)) {
    return {
      id: skillId,
      code: skillId,
      name: body.name || skillId,
      category: body.category || 'document_generation',
      description: body.description || '',
      visibility: 'private',
      status: 'draft',
      currentVersion: '1.0.1',
      promptText: body.promptText,
      sopText: body.sopText,
      inputSchemaJson: body.inputSchemaJson,
      toolPermissionsJson: body.toolPermissionsJson,
      outputContractJson: body.outputContractJson,
    };
  }
  return unwrap(
    request<Result<DocumentSkillDetail>>(
      http.put(`/v1/document-skills/${skillId}/content`, body),
    ),
  );
}

export async function importDocumentSkill(
  projectId: string,
  body: ImportDocumentSkillRequest,
): Promise<ImportDocumentSkillResult> {
  if (useMock(projectId)) {
    return {
      skill: {
        id: `skill-import-${Date.now()}`,
        code: body.code || 'imported',
        name: body.name || '导入的 Skill',
        category: 'document_generation',
        description: '本地导入',
        visibility: 'private',
        status: 'draft',
        currentVersion: '0.1.0',
        promptText: body.packageContent ? 'imported prompt' : undefined,
      },
      warnings: body.packageContent || body.packageBase64 ? [] : ['mock：未提供 Skill 包'],
      importMode: body.packageBase64 ? 'package-zip' : body.packageContent ? 'package' : 'clone',
    };
  }
  return unwrap(
    request<Result<ImportDocumentSkillResult>>(
      http.post(`/v1/projects/${projectId}/document-skills/import`, body),
    ),
  );
}

/** 上传标准 Skill ZIP 包导入 */
export async function importDocumentSkillPackageFile(
  projectId: string,
  file: File,
  options?: { code?: string; name?: string },
): Promise<ImportDocumentSkillResult> {
  if (useMock(projectId)) {
    return importDocumentSkill(projectId, {
      code: options?.code,
      name: options?.name || file.name.replace(/\.zip$/i, ''),
      packageContent: '{"name":"mock zip import"}',
    });
  }
  const form = new FormData();
  form.append('file', file);
  if (options?.code?.trim()) form.append('code', options.code.trim());
  if (options?.name?.trim()) form.append('name', options.name.trim());
  return unwrap(
    request<Result<ImportDocumentSkillResult>>(
      http.post(`/v1/projects/${projectId}/document-skills/import-package`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    ),
  );
}

/** 导出标准 Skill ZIP 包 */
export async function downloadDocumentSkillPackage(
  skillId: string,
  fileNameHint?: string,
): Promise<void> {
  if (useMock(undefined, skillId)) {
    const blob = new Blob(['mock skill package'], { type: 'application/zip' });
    triggerBlobDownload(blob, `${fileNameHint || 'skill'}.zip`);
    return;
  }
  const res = await http.get(`/v1/document-skills/${skillId}/export`, { responseType: 'blob' });
  const blob =
    res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/zip' });
  if (blob.type && blob.type.includes('application/json')) {
    const text = await blob.text();
    throw new Error(text || '导出失败');
  }
  let fileName = (fileNameHint || `skill-${skillId}`).trim() || `skill-${skillId}`;
  if (!/\.zip$/i.test(fileName)) fileName = `${fileName}.zip`;
  const disposition = String(res.headers?.['content-disposition'] || '');
  const m = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  if (m) {
    try {
      fileName = decodeURIComponent((m[1] || m[2] || fileName).trim());
    } catch {
      /* keep */
    }
  }
  triggerBlobDownload(blob, fileName);
}

/** 运行标准包验收用例 */
export async function runDocumentSkillAcceptance(
  skillId: string,
  provider?: string,
): Promise<SkillAcceptanceRunResult> {
  if (useMock(undefined, skillId)) {
    return {
      skillId,
      provider: provider || 'mock',
      total: 2,
      passed: 2,
      failed: 0,
      skipped: 0,
      cases: [
        { id: 'positive-trigger-create', status: 'passed', expectedDecisionCode: 'SUPPORTED_GENERATION' },
        { id: 'negative-trigger-out-of-scope', status: 'passed', expectedDecisionCode: 'OUT_OF_SCOPE' },
      ],
      warnings: [],
    };
  }
  const qs = provider ? `?provider=${encodeURIComponent(provider)}` : '';
  return unwrap(
    request<Result<SkillAcceptanceRunResult>>(
      http.post(`/v1/document-skills/${skillId}/acceptance-run${qs}`),
    ),
  );
}

function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function publishDocumentSkill(
  skillId: string,
): Promise<{ skillId: string; version: string; status: string }> {
  if (useMock(undefined, skillId)) {
    return { skillId, version: '1.0.0', status: 'published' };
  }
  return unwrap(
    request<Result<{ skillId: string; version: string; status: string }>>(
      http.post(`/v1/document-skills/${skillId}/publish`),
    ),
  );
}

export async function updateDocumentSkillStatus(
  skillId: string,
  status: string,
): Promise<DocumentSkillDetail> {
  if (useMock(undefined, skillId)) {
    return {
      id: skillId,
      code: skillId,
      name: skillId,
      category: 'document_generation',
      description: '',
      visibility: 'private',
      status,
      currentVersion: '1.0.0',
    };
  }
  return unwrap(
    request<Result<DocumentSkillDetail>>(
      http.patch(`/v1/document-skills/${skillId}/status`, { status }),
    ),
  );
}

/** —— Skill 执行 —— */

export async function startSkillExecution(
  projectId: string,
  body: StartSkillExecutionRequest,
  opts?: { signal?: AbortSignal },
): Promise<SkillExecutionDetail> {
  if (useMock(projectId)) {
    return {
      executionId: `exec-mock-${Date.now()}`,
      status: 'completed',
      skillId: body.skillId,
      documentId: body.documentId,
      sessionId: `session-${Date.now()}`,
      resultHtml: `<h3>生成预览</h3><p>${body.userInput || '已根据 Skill 与项目资料生成草稿内容。'}</p><p>引用 [1]</p>`,
      citations: [
        {
          index: 1,
          title: '项目需求说明',
          snippet: '示例引用片段，用于演示 Citation 回链。',
          source: 'project-knowledge',
          score: 0.91,
        },
      ],
    };
  }
  return unwrap(
    request<Result<SkillExecutionDetail>>(
      http.post(`/v1/projects/${projectId}/skill-executions`, body, {
        signal: opts?.signal,
        timeout: 120000,
      }),
    ),
  );
}

export async function getSkillExecution(executionId: string): Promise<SkillExecutionDetail> {
  if (useMock(undefined, executionId) || executionId.startsWith('exec-mock-')) {
    return {
      executionId,
      status: 'completed',
      resultHtml: '<p>mock execution</p>',
      citations: [],
    };
  }
  return unwrap(
    request<Result<SkillExecutionDetail>>(
      http.get(`/v1/skill-executions/${executionId}`),
    ),
  );
}

export async function cancelSkillExecution(
  executionId: string,
): Promise<{ executionId: string; status: string }> {
  if (useMock(undefined, executionId) || executionId.startsWith('exec-mock-')) {
    return { executionId, status: 'cancelled' };
  }
  return unwrap(
    request<Result<{ executionId: string; status: string }>>(
      http.post(`/v1/skill-executions/${executionId}/cancel`),
    ),
  );
}

export async function applySkillExecution(
  executionId: string,
  body?: ApplySkillExecutionRequest,
): Promise<ApplySkillExecutionResult> {
  if (useMock(undefined, executionId) || executionId.startsWith('exec-mock-')) {
    return {
      documentId: body?.documentId || `doc-${Date.now()}`,
      title: body?.title || 'AI 生成文档',
      applied: true,
      createdNew: Boolean(body?.createNewDocument),
    };
  }
  return unwrap(
    request<Result<ApplySkillExecutionResult>>(
      http.post(`/v1/skill-executions/${executionId}/apply`, body || {}),
    ),
  );
}

export async function resolveSkillApplyTarget(
  executionId: string,
  body?: ResolveSkillApplyTargetRequest,
): Promise<ResolveSkillApplyTargetResult> {
  if (useMock(undefined, executionId) || executionId.startsWith('exec-mock-')) {
    if (!body?.currentDocumentId) {
      return { decision: 'create_new', confidence: 'high', reason: 'mock: no current doc' };
    }
    return {
      decision: 'clarify',
      confidence: 'low',
      clarifyQuestion: '请问是要新建一份文档，还是修改当前打开的文档？请回复「新建文档」或「修改当前文档」。',
      reason: 'mock clarify',
    };
  }
  return unwrap(
    request<Result<ResolveSkillApplyTargetResult>>(
      http.post(`/v1/skill-executions/${executionId}/resolve-apply-target`, body || {}),
    ),
  );
}

/** —— 模版多轮生成 —— */

export async function startTemplateGeneration(
  projectId: string,
  body: StartTemplateGenerationRequest,
  opts?: { signal?: AbortSignal },
): Promise<TemplateGenerationResult> {
  if (useMock(projectId)) {
    const missing = !(body.userMessage || '').includes('项目名称')
      ? ['projectName']
      : [];
    return {
      generationId: `tpl-gen-mock-${Date.now()}`,
      status: missing.length ? 'collecting' : 'completed',
      assistantReply: missing.length
        ? '模版还需要以下信息才能生成：\n1. 项目名称\n\n请直接回复，例如：项目名称：XX'
        : '已根据模版骨架生成文档内容。确认后将创建并打开工作台文档。',
      missingFields: missing,
      filledSlots: missing.length ? {} : { projectName: '示例项目' },
      templateId: body.templateId,
      templateTitle: 'Mock 模版',
      resultHtml: missing.length ? undefined : '<h1>示例项目</h1><p>已按模版生成</p>',
    };
  }
  return unwrap(
    request<Result<TemplateGenerationResult>>(
      http.post(`/v1/projects/${projectId}/template-generations/start`, body, {
        signal: opts?.signal,
        timeout: 180000,
      }),
    ),
  );
}

export async function messageTemplateGeneration(
  projectId: string,
  generationId: string,
  body: TemplateGenerationMessageRequest,
  opts?: { signal?: AbortSignal },
): Promise<TemplateGenerationResult> {
  if (useMock(projectId) || generationId.startsWith('tpl-gen-mock-')) {
    return {
      generationId,
      status: 'completed',
      assistantReply: '已根据模版骨架生成文档内容。确认后将创建并打开工作台文档。',
      missingFields: [],
      filledSlots: { projectName: body.userMessage || '示例项目' },
      templateId: '0',
      templateTitle: 'Mock 模版',
      resultHtml: `<h1>${body.userMessage || '示例项目'}</h1><p>已按模版生成</p>`,
    };
  }
  return unwrap(
    request<Result<TemplateGenerationResult>>(
      http.post(`/v1/projects/${projectId}/template-generations/${generationId}/message`, body, {
        signal: opts?.signal,
        timeout: 180000,
      }),
    ),
  );
}

export async function confirmTemplateGeneration(
  projectId: string,
  generationId: string,
  opts?: { signal?: AbortSignal },
): Promise<TemplateGenerationResult> {
  if (useMock(projectId) || generationId.startsWith('tpl-gen-mock-')) {
    return {
      generationId,
      status: 'completed',
      assistantReply: '文档已创建',
      missingFields: [],
      filledSlots: {},
      documentId: `doc-mock-${Date.now()}`,
      documentTitle: 'Mock 模版（生成）',
      resultHtml: '<h1>已生成</h1>',
    };
  }
  return unwrap(
    request<Result<TemplateGenerationResult>>(
      http.post(`/v1/projects/${projectId}/template-generations/${generationId}/confirm`, {}, {
        signal: opts?.signal,
        timeout: 120000,
      }),
    ),
  );
}

export async function getTemplateGeneration(
  projectId: string,
  generationId: string,
): Promise<TemplateGenerationResult> {
  return unwrap(
    request<Result<TemplateGenerationResult>>(
      http.get(`/v1/projects/${projectId}/template-generations/${encodeURIComponent(generationId)}`),
    ),
  );
}

export type TemplateGenerationStreamEvent =
  | { type: 'progress'; data: Record<string, unknown> }
  | { type: 'done'; data: TemplateGenerationResult }
  | { type: 'error'; data: { message?: string } };

function normalizeTemplateGenerationResult(raw: Record<string, unknown>): TemplateGenerationResult {
  return {
    generationId: String(raw.generationId ?? ''),
    status: String(raw.status ?? 'failed'),
    assistantReply: raw.assistantReply != null ? String(raw.assistantReply) : undefined,
    missingFields: (raw.missingFields as string[] | undefined) ?? [],
    filledSlots: (raw.filledSlots as Record<string, string> | undefined) ?? {},
    templateId: raw.templateId != null ? String(raw.templateId) : undefined,
    templateTitle: raw.templateTitle != null ? String(raw.templateTitle) : undefined,
    resultHtml: raw.resultHtml != null ? String(raw.resultHtml) : undefined,
    resultMarkdown: raw.resultMarkdown != null ? String(raw.resultMarkdown) : undefined,
    documentId: raw.documentId != null ? String(raw.documentId) : undefined,
    documentTitle: raw.documentTitle != null ? String(raw.documentTitle) : undefined,
    errorMessage: raw.errorMessage != null ? String(raw.errorMessage) : undefined,
    matchHits: raw.matchHits as TemplateGenerationResult['matchHits'],
    referenceSummaries: raw.referenceSummaries as TemplateGenerationResult['referenceSummaries'],
    usedSourceFormat: Boolean(raw.usedSourceFormat),
    processSteps: raw.processSteps as TemplateGenerationResult['processSteps'],
    intentSummary: raw.intentSummary != null ? String(raw.intentSummary) : undefined,
    fillInstructions: raw.fillInstructions != null ? String(raw.fillInstructions) : undefined,
    outlineSummary: raw.outlineSummary != null ? String(raw.outlineSummary) : undefined,
    validationIssueCount: raw.validationIssueCount != null ? Number(raw.validationIssueCount) : undefined,
    pendingConfirmCount: raw.pendingConfirmCount != null ? Number(raw.pendingConfirmCount) : undefined,
    qualitySummary: raw.qualitySummary != null ? String(raw.qualitySummary) : undefined,
    reviewChecklist: Array.isArray(raw.reviewChecklist)
      ? (raw.reviewChecklist as TemplateGenerationResult['reviewChecklist'])
      : undefined,
    generationMode: raw.generationMode != null ? String(raw.generationMode) : undefined,
    requiredContextAsked: raw.requiredContextAsked != null ? Boolean(raw.requiredContextAsked) : undefined,
  };
}

function consumeTemplateGenerationSse(
  urlPath: string,
  body: unknown,
  options: {
    onEvent: (event: TemplateGenerationStreamEvent) => void;
    signal?: AbortSignal;
  },
): Promise<TemplateGenerationResult> {
  return new Promise((resolve, reject) => {
    const token = getBrowserStoredAccessToken();
    const base = http.defaults.baseURL || '/api';
    const url = `${base}${urlPath}`;
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    fetch(fullUrl, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body ?? {}),
      signal: options.signal,
    })
      .then((res) => {
        if (!res.ok) {
          res.text().then((t) => {
            let msg = `模版生成失败（HTTP ${res.status}）`;
            try {
              const j = JSON.parse(t) as { message?: string };
              if (j?.message) msg = j.message;
            } catch {
              if (t?.trim()) msg = t.trim().slice(0, 200);
            }
            reject(new Error(msg));
          });
          return;
        }
        const reader = res.body?.getReader();
        if (!reader) {
          reject(new Error('流式响应不可用'));
          return;
        }
        const decoder = new TextDecoder();
        let buf = '';
        let sseEventName = '';
        let finalResult: TemplateGenerationResult | null = null;

        function dispatch(eventName: string, json: string) {
          if (!json || json === '[DONE]') return;
          let raw: Record<string, unknown>;
          try {
            raw = JSON.parse(json) as Record<string, unknown>;
          } catch {
            return;
          }
          if (eventName === 'progress') {
            options.onEvent({ type: 'progress', data: raw });
            return;
          }
          if (eventName === 'error') {
            options.onEvent({ type: 'error', data: raw as { message?: string } });
            reject(new Error(String((raw as { message?: string }).message || '模版生成失败')));
            return;
          }
          if (eventName === 'done') {
            finalResult = normalizeTemplateGenerationResult(raw);
            options.onEvent({ type: 'done', data: finalResult });
          }
        }

        function flushCompleteLines(streamDone: boolean) {
          const lines = buf.split('\n');
          buf = streamDone ? '' : (lines.pop() ?? '');
          for (const line of lines) {
            if (!line) {
              sseEventName = '';
              continue;
            }
            if (line.startsWith('event:')) {
              sseEventName = line.slice(6).trim();
              continue;
            }
            const m = line.match(/^data:\s?(.*)$/);
            if (m) dispatch(sseEventName || 'message', m[1] ?? '');
          }
        }

        function pump(): Promise<void> {
          return reader.read().then(({ done, value }) => {
            if (done) {
              flushCompleteLines(true);
              if (finalResult) {
                resolve(finalResult);
                return;
              }
              reject(new Error('模版生成流式响应未正常结束（连接可能已中断）'));
              return;
            }
            buf += decoder.decode(value, { stream: true });
            flushCompleteLines(false);
            return pump();
          });
        }

        pump().catch(reject);
      })
      .catch(reject);
  });
}

/** 流式开始模版生成：SSE progress / done / error */
export function consumeTemplateGenerationStartStream(
  projectId: string,
  body: StartTemplateGenerationRequest,
  options: {
    onEvent: (event: TemplateGenerationStreamEvent) => void;
    signal?: AbortSignal;
  },
): Promise<TemplateGenerationResult> {
  if (useMock(projectId)) {
    return startTemplateGeneration(projectId, body, { signal: options.signal }).then((result) => {
      options.onEvent({ type: 'done', data: result });
      return result;
    });
  }
  return consumeTemplateGenerationSse(
    `/v1/projects/${projectId}/template-generations/start/stream`,
    body,
    options,
  );
}

/** 流式继续模版生成对话 */
export function consumeTemplateGenerationMessageStream(
  projectId: string,
  generationId: string,
  body: TemplateGenerationMessageRequest,
  options: {
    onEvent: (event: TemplateGenerationStreamEvent) => void;
    signal?: AbortSignal;
  },
): Promise<TemplateGenerationResult> {
  if (useMock(projectId) || generationId.startsWith('tpl-gen-mock-')) {
    return messageTemplateGeneration(projectId, generationId, body, { signal: options.signal }).then((result) => {
      options.onEvent({ type: 'done', data: result });
      return result;
    });
  }
  return consumeTemplateGenerationSse(
    `/v1/projects/${projectId}/template-generations/${encodeURIComponent(generationId)}/message/stream`,
    body,
    options,
  );
}
